import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BundleGeneratorTab from './BundleGeneratorTab';
import CampaignReportsTab from './CampaignReportsTab';
import AdBenchmarksTab from './AdBenchmarksTab';
import CurrentCampaignsTab from './CurrentCampaignsTab';
import { Tabs, DashboardFrame, Toast, ToastKind } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { AdOpsDashboardTabKey, adOpsDashboardTabs, needsCampaignReports } from './adOpsDashboardTabs';

const TOAST_MS = 5000;

const AdOpsDashboard: React.FC = () => {
  const { has, loading: permissionsLoading } = usePermissions();
  const canCampaignReports = has('adOps.campaignReports.manage');

  // Campaign Reports is the first tab for permitted users; everyone on this
  // dashboard already passed the adOps.view gate in App.tsx.
  const [activeTab, setActiveTab] = useState<AdOpsDashboardTabKey>('campaign-reports');

  const tabOptions = useMemo(() => adOpsDashboardTabs(canCampaignReports), [canCampaignReports]);

  // Snap off the default tab if permissions resolve without the key. Current
  // Campaigns needs only adOps.view, so every user on this dashboard can open it.
  useEffect(() => {
    if (!permissionsLoading && !canCampaignReports && needsCampaignReports(activeTab)) {
      setActiveTab('current-campaigns');
    }
  }, [permissionsLoading, canCampaignReports, activeTab]);

  const [toast, setToast] = useState<{ kind: ToastKind; title: string; message?: string } | null>(null);
  const notify = useCallback((kind: 'success' | 'error', title: string, message?: string) => {
    setToast({ kind, title, message });
    window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const renderTab = () => {
    if (permissionsLoading && activeTab === 'campaign-reports') return null;
    switch (activeTab) {
      case 'campaign-reports':
        return canCampaignReports ? <CampaignReportsTab notify={notify} /> : null;
      // Same component for both benchmark sets: the key keeps one set's
      // unsaved draft from carrying over into the other tab.
      case 'ad-benchmarks':
        return canCampaignReports ? <AdBenchmarksTab key="standard" set="standard" notify={notify} /> : null;
      case 'sov-benchmarks':
        return canCampaignReports ? <AdBenchmarksTab key="sov" set="sov" notify={notify} /> : null;
      // Bundle Generator is hidden (see HIDDEN_AD_OPS_TABS in adOpsDashboardTabs.ts),
      // so nothing selects this tab; the case stays so restoring it is a one-line change.
      case 'bundle-generator':
        return <BundleGeneratorTab />;
      case 'current-campaigns':
        return <CurrentCampaignsTab />;
      default:
        return null;
    }
  };

  return (
    <DashboardFrame
      eyebrow="Ad Ops · GAM automation"
      title="Ad Ops Console"
      tabs={
        <Tabs<AdOpsDashboardTabKey>
          value={activeTab}
          onChange={setActiveTab}
          variant="underlined"
          options={tabOptions}
        />
      }
    >
      {renderTab()}
      {toast && <Toast kind={toast.kind} title={toast.title} message={toast.message} onClose={() => setToast(null)} />}
    </DashboardFrame>
  );
};

export default AdOpsDashboard;
